import React, { useEffect, useState } from 'react';

interface Props {
	API_BASE: string;
	authHeaders: () => Record<string, string>;
	/**
	 * When true, the "Users with Accounts" count comes back from the backend
	 * pre-filtered to exclude accounts with isTestAccount=true or
	 * isInternalAccount=true, and the tile title gets a "(excluding Test &
	 * Internal Accounts)" subtitle. False (dev) leaves everything unfiltered
	 * since most dev users are test accounts.
	 */
	isProd: boolean;
}

export const MetricsSection: React.FC<Props> = ({ API_BASE, authHeaders, isProd }) => {
	const [metrics, setMetrics] = useState<{
		totalUsers: number;
		pendingInvites: number;
		waitlistNoInvite: number;
	} | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchMetrics = async () => {
			setLoading(true);
			setError(null);
			try {
				// Single pre-aggregated count call (server-side scan with
				// Select:'COUNT' + prod-only test/internal filter) — replaces the
				// previous "fetch every user 10 at a time just to count them" loop
				// that was 15+ round-trips at ~150 users and scaled linearly.
				const userMetricsRes = await fetch(`${API_BASE}/admin/user-metrics`, { headers: authHeaders() });
				const userMetricsData = await userMetricsRes.json();
				const totalUsers: number = typeof userMetricsData.totalUsers === 'number' ? userMetricsData.totalUsers : 0;

				// Fetch all pending invites (response shape: { invites: [...] })
				const invitesRes = await fetch(`${API_BASE}/admin/invite`, { headers: authHeaders() });
				const invitesData = await invitesRes.json();
				const pendingInvites = Array.isArray(invitesData.invites) ? invitesData.invites.length : 0;

				// Fetch waitlist and filter by status == 'awaiting invite' (response shape: { waitlist: [...] })
				const waitlistRes = await fetch(`${API_BASE}/waitlist`, { headers: authHeaders() });
				const waitlistData = await waitlistRes.json();
				let waitlistNoInvite = 0;
				if (Array.isArray(waitlistData.waitlist)) {
					waitlistNoInvite = waitlistData.waitlist.filter((item: any) => item.status === 'awaiting invite').length;
				}

				setMetrics({
					totalUsers,
					pendingInvites,
					waitlistNoInvite,
				});
			} catch (err: any) {
				setError(err.message || 'Failed to fetch metrics.');
			} finally {
				setLoading(false);
			}
		};
		fetchMetrics();
	}, [API_BASE, authHeaders]);

	return (
		<section className="dashboard-card" style={{ gridColumn: 'span 1', marginBottom: 0 }}>
			<h2 style={{ marginBottom: 0 }}>
				Platform Metrics
				{isProd && (
					<span style={{ display: 'block', fontSize: '0.65em', fontWeight: 400, color: '#666', marginTop: 2 }}>
						(excludes internal &amp; test accounts)
					</span>
				)}
			</h2>
			{loading && <p>Loading metrics...</p>}
			{error && <p style={{ color: 'red' }}>{error}</p>}
			{metrics && (
				<ul style={{ listStyle: 'none', padding: 0 }}>
					<li><strong>Users with Accounts:</strong> {metrics.totalUsers}</li>
					<li><strong>Pending Invites:</strong> {metrics.pendingInvites}</li>
					<li><strong>Waitlist (No Invite):</strong> {metrics.waitlistNoInvite}</li>
				</ul>
			)}
		</section>
	);
};

export default MetricsSection;
